"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { HelpCircle, Search, Scale, Building2, FileCheck2, CheckCircle2, ArrowRight, Target, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function BuyerIntent() {
  const shouldReduceMotion = useReducedMotion();
  const [activeStageId, setActiveStageId] = useState<string>("problem");
  const buyerStages = [{
    id: "problem",
    label: "Problem awareness",
    icon: HelpCircle,
    buyerActivity: "The prospect recognises an internal inefficiency, operational constraint, or technical roadblock but hasn't yet formalised the category of solution needed.",
    examples: ["why are my manufacturing lead times so long", "B2B procurement challenges UK"]
  }, {
    id: "solution",
    label: "Solution research",
    icon: Search,
    buyerActivity: "The prospect knows the class of product or service required and is researching technical methods, software categories, or specialised partners.",
    examples: ["workflow automation software for engineering teams", "industrial air compressor maintenance services"]
  }, {
    id: "comparison",
    label: "Comparison",
    icon: Scale,
    buyerActivity: "The buying group explores market alternatives, shortlisting reputable providers and evaluating methodologies side by side.",
    examples: ["top SEO consultants for B2B SaaS UK", "best supply chain analytics tools vs alternatives"]
  }, {
    id: "evaluation",
    label: "Supplier evaluation",
    icon: Building2,
    buyerActivity: "Direct assessment between shortlisted specialists to verify fit against security, team size, industry track record, and operational standards.",
    examples: ["software A vs software B for mid-market finance teams", "how to choose an SEO consultant for a UK manufacturer"]
  }, {
    id: "purchase",
    label: "Purchase readiness",
    icon: FileCheck2,
    buyerActivity: "Senior stakeholders and procurement leads are preparing commercial terms, pricing expectations, or formal request-for-proposal documents.",
    examples: ["enterprise pricing for supply chain management platform", "RFP template for specialised SEO consultancy provider"]
  }];
  const currentStage = buyerStages.find(stage => stage.id === activeStageId) || buyerStages[0];
  const prioritisationCriteria = [{
    number: "01",
    title: "Commercial relevance to your offer",
    description: "Queries directly connected to high-margin services, flagship software solutions, and services your sales team is actively resourced to deliver."
  }, {
    number: "02",
    title: "Buyer stage and enquiry likelihood",
    description: "Focusing effort on evaluation and purchase-readiness searches that yield qualified commercial enquiries rather than passive research views."
  }, {
    number: "03",
    title: "Fit with ideal customer profiles",
    description: "Prioritising search phrasing and industry vocabulary used by key decision-makers within your target sectors and account tiers."
  }, {
    number: "04",
    title: "Feasibility and competitor landscape",
    description: "Analysing existing domain authority, current content depth, and competitor strength to pursue commercial positions where visibility is attainable."
  }, {
    number: "05",
    title: "Long-tail cluster opportunities",
    description: "Building semantic authority across tightly grouped problem-and-solution clusters rather than banking on a single high-competition head keyword."
  }];
  return <section id="b2bbuyerintent" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Commercial Intent Architecture
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            B2B keyword research and buyer intent
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Effective B2B keyword research focuses on search intent and account context rather than
            misleading search volume. B2B purchases involve multiple decision-makers across a lengthy
            evaluation. I map search queries across the complete buyer journey to ensure your website
            answers high-intent commercial questions at every evaluation touchpoint.
          </p>
        </div>

        {/* Interactive Segmented Stages */}
        <div className="mb-16">
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
            {buyerStages.map(stage => {
            const Icon = stage.icon;
            const isActive = stage.id === activeStageId;
            return <Button key={stage.id} variant={isActive ? "default" : "outline"} size="sm" onClick={() => setActiveStageId(stage.id)} className={`rounded-full px-4 sm:px-5 transition-all text-xs sm:text-sm font-medium flex items-center gap-2 ${isActive ? "shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>
                  <Icon className="size-3.5" />
                  <span>{stage.label}</span>
                </Button>;
          })}
          </div>

          {/* Active Stage Detail Panel */}
          <motion.div key={currentStage.id} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} className="max-w-4xl mx-auto">
            <Card className="border border-border/80 bg-card shadow-xs">
              <CardHeader className="pb-4 border-b border-border/60">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <currentStage.icon className="size-5" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                        Buyer Stage
                      </span>
                      <CardTitle className="text-xl font-semibold font-default text-foreground">
                        {currentStage.label}
                      </CardTitle>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground self-start sm:self-center font-mono">
                    Illustrative examples, not client data
                  </span>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                    What the buyer is doing
                  </h4>
                  <p className="text-sm sm:text-base text-foreground leading-relaxed">
                    {currentStage.buyerActivity}
                  </p>
                </div>

                <div className="pt-4 border-t border-border/60">
                  <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
                    Sample search queries mapped to this stage
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {currentStage.examples.map((example, idx) => <div key={`example-${idx}`} className="p-3.5 rounded-md border border-border bg-background/80 text-sm font-mono text-foreground flex items-center gap-2.5">
                        <Search className="size-3.5 text-primary shrink-0" />
                        <span className="truncate">"{example}"</span>
                      </div>)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* How I Prioritise B2B Keywords */}
        <div className="mt-16 pt-12 border-t border-border/70">
          <div className="max-w-3xl mb-10">
            <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground">
              How I prioritise B2B keywords
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Keyword prioritisation feeds directly into content roadmaps, commercial landing page designs,
              and technical on-page updates. I weigh five clear strategic factors before committing resources:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {prioritisationCriteria.map((item, i) => <Card key={`criteria-${i}`} className="border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-primary/10 text-primary">
                      {item.number}
                    </span>
                    <Target className="size-4 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-base font-semibold text-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* Link Row */}
        <div className="mt-14 pt-8 border-t border-border/60 text-center">
          <p className="text-sm text-muted-foreground">
            Uncover where your current commercial pages stand and identify immediate intent gaps with{" "}
            <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium inline-flex items-center gap-1">
              my SEO audit
              <ArrowRight className="size-3.5" />
            </Link>
          </p>
        </div>
      </div>
    </section>;
}